import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Monitor, Smartphone, Building2, ShieldCheck, MapPin, FileSpreadsheet, ArrowRight, UserPlus, Clock, CalendarClock, History, QrCode, Bell, Camera, Check } from "lucide-react";
import { Logo } from "../components/Brand";
import { LegalFooter } from "../components/LegalFooter";

const modes = [
  { key: "kiosk", to: "/kiosk", icon: Monitor, title: "Kiosk Mode", desc: "Shared tablet at the door. Staff punch in with a PIN and a quick selfie.", tag: "On-site" },
  { key: "mobile", to: "/mobile", icon: Smartphone, title: "Mobile Clock", desc: "Clock in from your phone with GPS geofence verification.", tag: "GPS" },
  { key: "admin", to: "/login", icon: Building2, title: "Company Admin", desc: "Manage staff, timesheets, pay & branding for your company.", tag: "Sign in" },
  { key: "super", to: "/login", icon: ShieldCheck, title: "Super Admin", desc: "Onboard companies, issue API keys, monitor the platform.", tag: "Sign in" },
];

const platform = [
  {
    icon: Clock,
    tag: "Module 1",
    title: "Time Clock",
    desc: "Kiosk or mobile punch-in with PIN + selfie, GPS geofencing, scheduling, overtime math and one-click payroll exports.",
    points: [
      "Shared kiosk & mobile GPS clock-in",
      "Employee scheduling, shift swaps & availability",
      "Timesheets, approvals & full edit audit trail",
      "Payroll CSV + QuickBooks / ADP / Paychex exports",
    ],
  },
  {
    icon: UserPlus,
    tag: "Module 2",
    title: "Visitor Management",
    desc: "Sign guests in at the door, snap a photo, notify their host and print a badge — with a live log of everyone on-site.",
    points: [
      "Guest check-in with photo capture",
      "Automatic visitor badge printing",
      "Host notifications on arrival",
      "On-site visitor log & badge reprint",
    ],
  },
];

const features = [
  { icon: MapPin, title: "GPS Geofencing", desc: "Verify mobile punches fall inside the work-zone radius." },
  { icon: CalendarClock, title: "Scheduling", desc: "Templates, shift swapping, overtime & coverage alerts, printable rosters." },
  { icon: History, title: "Edit Audit Trail", desc: "Every timesheet change logged with who, what & when for disputes." },
  { icon: FileSpreadsheet, title: "Payroll & Exports", desc: "Overtime math, raw punch + summary CSV, QuickBooks / ADP / Paychex." },
  { icon: QrCode, title: "QR Clock Posters", desc: "Branded printable posters with the clock QR code for the break room." },
  { icon: Bell, title: "Missed-Punch Alerts", desc: "Nightly email to managers listing anyone who forgot to clock out." },
  { icon: Camera, title: "Webcam Controls", desc: "Capture selfies on clock-in, with a master switch to disable everywhere." },
  { icon: Building2, title: "Multi-Tenant Branding", desc: "Every company isolated with its own logo, colors, URLs & account number." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 80% 0%, rgba(29,84,196,0.18), transparent 70%)" }} />
      <header className="relative max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Logo size="lg" />
        <button
          data-testid="landing-signin-button"
          onClick={() => navigate("/login")}
          className="px-5 py-2.5 rounded-full bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-sm font-semibold transition-colors"
        >
          Sign in
        </button>
      </header>

      <section className="relative max-w-7xl mx-auto px-6 pt-10 pb-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" /> Time Clock · Visitor Management
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Clock in the <span className="text-blue-500">right</span> way.<br />Welcome guests the <span className="text-blue-500">right</span> way.
          </h1>
          <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-xl">
            RightPay is a multi-tenant platform with two modules — a payroll <b className="text-slate-200">Time Clock</b> for your team and <b className="text-slate-200">Visitor Management</b> for your front desk. Run a shared kiosk, let staff punch from their phones with GPS, then export payroll in a click.
          </p>
        </motion.div>

        {/* Two modules */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-14" data-testid="landing-modules">
          {platform.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`landing-module-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 + i * 0.1 }}
              className="bg-slate-900/80 border border-slate-800 rounded-3xl p-8 relative overflow-hidden"
            >
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-blue-500/5 blur-2xl" />
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <p.icon className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{p.tag}</span>
                  <h3 className="text-2xl font-bold leading-tight">{p.title}</h3>
                </div>
              </div>
              <p className="text-slate-400 leading-relaxed mb-6">{p.desc}</p>
              <ul className="space-y-2.5">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" /> {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Access modes */}
        <h2 className="text-lg font-semibold text-slate-300 mt-16 mb-4">Jump in</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {modes.map((m, i) => (
            <motion.button
              key={m.key}
              data-testid={`landing-mode-${m.key}`}
              onClick={() => navigate(m.to)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
              whileHover={{ y: -4 }}
              className="group text-left bg-slate-900/80 border border-slate-800 hover:border-blue-500/50 rounded-2xl p-6 transition-colors relative overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <m.icon className="w-6 h-6 text-blue-400" />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border border-slate-700 rounded-full px-2 py-0.5">{m.tag}</span>
              </div>
              <h3 className="text-lg font-bold">{m.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{m.desc}</p>
              <div className="flex items-center gap-1 text-blue-400 text-sm font-semibold mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                Open <ArrowRight className="w-4 h-4" />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Feature grid */}
        <h2 className="text-lg font-semibold text-slate-300 mt-16 mb-6">Everything included</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="flex gap-4">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold">{f.title}</h4>
                <p className="text-sm text-slate-400 mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <LegalFooter className="relative" />
    </div>
  );
}
